import React, { useState } from "react";
import { X, Check, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Host reviews a player's payment proof.
 * Props: matchId, entry { user_id, name, picture, status, proof_image, player_notes, uploaded_at }, onClose, onUpdated()
 */
export default function PaymentReviewModal({ matchId, entry, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");

  const review = async (action) => {
    if (action === "reject" && !rejecting) {
      setRejecting(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/matches/${matchId}/payment/review`, {
        user_id: entry.user_id,
        action,
        note: action === "reject" ? note : undefined,
      });
      toast.success(action === "approve" ? "Approved!" : "Ditolak");
      onUpdated && (await onUpdated());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-card border border-border rounded-t-3xl sm:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="payment-review-modal">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div className="min-w-0">
            <div className="font-heading text-lg font-black tracking-tight">Review Bukti</div>
            <div className="text-xs text-muted-foreground truncate">dari {entry.name}</div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center" data-testid="review-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {entry.proof_image ? (
            <a href={entry.proof_image} target="_blank" rel="noopener noreferrer" className="block rounded-2xl overflow-hidden border border-border bg-secondary">
              <img src={entry.proof_image} alt="proof" className="w-full max-h-80 object-contain" />
            </a>
          ) : (
            <div className="bg-secondary border border-border rounded-2xl py-10 text-center text-sm text-muted-foreground">
              Tidak ada bukti gambar
            </div>
          )}

          {entry.player_notes && (
            <div className="bg-secondary/60 border border-border rounded-2xl p-3">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Catatan player</div>
              <div className="text-sm">{entry.player_notes}</div>
            </div>
          )}

          {rejecting && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Alasan tolak (optional)</label>
              <textarea
                data-testid="reject-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Misal: Nominal kurang, atau bukan rekening yang benar"
                className="mt-2 w-full bg-secondary border border-border rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => review("reject")}
              disabled={busy}
              data-testid="review-reject"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-destructive/15 text-destructive border border-destructive/30 font-bold active:scale-95"
            >
              {busy && rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
              {rejecting ? "Konfirmasi Tolak" : "Tolak"}
            </button>
            <button
              onClick={() => review("approve")}
              disabled={busy}
              data-testid="review-approve"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground font-bold glow-cyan active:scale-95"
            >
              {busy && !rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
